'use client';
import React, { createContext, useContext, useMemo } from 'react';
import { ProductSortConfig, ProductSortOption } from './types';
import { useProductSort } from './useProductSort';

type ProductSortContextValue = {
  /**
   * The editor's default until hydration, which is what the server rendered,
   * then whatever the URL asks for.
   */
  activeOption: ProductSortOption | undefined;
  /**
   * The one config on the page. The panel builds its dropdown from this rather
   * than from its own copy: two copies can disagree about which options exist,
   * and then the control offers a sort the ordering does not apply.
   */
  config: ProductSortConfig | undefined;
};

const ProductSortContext = createContext<ProductSortContextValue>({
  activeOption: undefined,
  config: undefined,
});

/**
 * The table and the cards are separate blocks in the dynamic zone, so neither
 * can see the panel's config alone. Extracted once upstream and published here,
 * which is what keeps their order identical.
 */
export const ProductSortProvider: React.FC<{
  sort?: ProductSortConfig;
  children: React.ReactNode;
}> = ({ sort, children }) => {
  const { activeOption } = useProductSort(sort);
  const value = useMemo(() => ({ activeOption, config: sort }), [activeOption, sort]);

  return (
    <ProductSortContext.Provider value={value}>
      {children}
    </ProductSortContext.Provider>
  );
};

/** Undefined when the page has no panel, so blocks keep their default order. */
export const useActiveProductSort = (): ProductSortOption | undefined =>
  useContext(ProductSortContext).activeOption;

/** The config the page was mounted with — the panel's own source for its list. */
export const useProductSortConfig = (): ProductSortConfig | undefined =>
  useContext(ProductSortContext).config;

